import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import { Box } from '@mui/material';
import { colors } from '@/globalStyles';
import { isColorOptionAvailable, isColorPrinterAvailable } from '@/helpers/printers';
import { updateImageContentThunk } from '@/actions';
import { MIDDLE, CENTER, DEFAULT_CONTRAST_VALUE } from '@/constants';
import { ImageContrastIcon, UploadIcon } from '@/components';
import { AutofillNumericInput } from '@/components/NumericInput';
import { Label } from '@/components/SideBar/styles';
import IconMinus from '@/icons/icon-cmme-minus-grey.svg';
import IconPlus from '@/icons/icon-cmme-plus-grey.svg';
import { ImageInsertWrapper, SectionImageInput, ImageInputWrapper } from './styles';

const InsertImage = props => {
  const {
    selectedSection,
    sectionFormatProps: { imageName },
    verticalAlignment,
    horizontalAlignment,
  } = props;
  const image = selectedSection?.image;
  const preselectedPrinter = useSelector((state) => state.core.preselectedPrinter);
  const printerList = useSelector((state) => state.core.printerList);
  const [isImageContrastHovered, setImageContrastHovered] = useState(false);
  const dispatch = useDispatch();
  const colorOptionAvailable = isColorOptionAvailable(preselectedPrinter) && isColorPrinterAvailable(printerList);
  const isImageContrastEnabled = !!image?.contrast;
  const imageContrastValue = image?.contrastValue;

  const handleFileInputChange = useCallback(e => {
    const imageFile = e.target.files[0];
    const reader = new FileReader();

    reader.readAsDataURL(imageFile);
    reader.onloadend = () => {
      const payload = {
        href: reader.result,
        source: reader.result,
        name: imageFile.name,
        horizontalAlignment: horizontalAlignment || CENTER,
        verticalAlignment: verticalAlignment || MIDDLE,
        contrast: false,
        contrastValue: `${DEFAULT_CONTRAST_VALUE}`,
      };

      if (colorOptionAvailable) {
        dispatch(updateImageContentThunk({
          isUndoable: true,
          settings: { pattern: 'CURRENT', value: 0, isNew: true },
          data: payload
        }));
      } else {
        if (isImageContrastEnabled) {
          const number = parseInt(imageContrastValue, 10);
          if (number) {
            dispatch(updateImageContentThunk({
              isUndoable: true,
              settings: { pattern: 'CONTRAST', value: number, isNew: true },
              data: { ...payload, contrast: image.contrast, contrastValue: image.contrastValue }
            }));
          }
        } else {
          dispatch(updateImageContentThunk({
            isUndoable: true,
            settings: { pattern: 'CONTRAST', value: DEFAULT_CONTRAST_VALUE, isNew: true },
            data: payload
          }));
        }
      }
    };

    e.target.value = '';
  }, []);

  const handleImageContrastClick = () => {
    if (image) {
      if (isImageContrastEnabled) {
        dispatch(updateImageContentThunk({
          settings: { pattern: 'CONTRAST', value: DEFAULT_CONTRAST_VALUE, isNew: false },
          data: { ...image, href: image.source, contrast: false }
        }));
      } else {
        const number = parseInt(image.contrastValue, 10);
        dispatch(updateImageContentThunk({
          settings: { pattern: 'CONTRAST', value: image.contrastValue ? number : DEFAULT_CONTRAST_VALUE, isNew: false },
          data: { ...image, contrast: true, contrastValue: `${image.contrastValue ? number : DEFAULT_CONTRAST_VALUE}` }
        }));
      }
    }
  };

  const handleImageContrastChange = value => {
    const number = parseInt(value, 10);
    if (!isNaN(number)) {
      if (image) {
        dispatch(updateImageContentThunk({
          settings: { pattern: 'CONTRAST', value: number, isNew: false },
          data: { ...image, contrast: true, contrastValue: value }
        }));
      }
    } else {
      dispatch(updateImageContentThunk({
        settings: { pattern: 'CURRENT', value: 0, isNew: false },
        data: { ...image, contrastValue: value }
      }));
    }
  };

  return (
    <>
      <ImageInsertWrapper>
        <Label>
          <FormattedMessage id="app.editor.sideMenu.header.image" defaultMessage="Image" />
        </Label>
        <SectionImageInput type="file" id="section-image-input" accept="image/*" onChange={handleFileInputChange} />
        <label htmlFor="section-image-input">
          <ImageInputWrapper>
            <Label>{imageName || <FormattedMessage id="app.editor.sideMenu.expansionPanel.uploadImage.placeholder" defaultMessage="Upload image..." />}</Label>
            <UploadIcon/>
          </ImageInputWrapper>
        </label>
      </ImageInsertWrapper>

      {!colorOptionAvailable && (
        <ImageInsertWrapper>
          <Label>
            <FormattedMessage id="cmme.imageContrast" defaultMessage="Image contrast" />
          </Label>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '50px', gap: '10px' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: colors.white,
                borderRadius: '4px',
                borderBottom: `2px solid ${isImageContrastEnabled ? colors.primary : colors.transparent}`,
                cursor: 'pointer',
              }}
              onClick={handleImageContrastClick}
              onMouseOver={setImageContrastHovered.bind(null, true)}
              onMouseOut={setImageContrastHovered.bind(null, false)}
            >
              <ImageContrastIcon isActive={isImageContrastEnabled || isImageContrastHovered} />
            </Box>
            {isImageContrastEnabled && (
              <Box sx={{ backgroundColor: colors.white, borderRadius: '4px' }}>
                <AutofillNumericInput
                  borderRadius="4px"
                  onMinusClick={handleImageContrastChange.bind(null, parseInt(imageContrastValue, 10) - 1)}
                  onInputChange={handleImageContrastChange}
                  onPlusClick={handleImageContrastChange.bind(null, parseInt(imageContrastValue, 10) + 1)}
                  value={imageContrastValue}
                  className="inputContainer"
                  backgroundColor={colors.white}
                  hoverColor={colors.whiteSmoke}
                  selectedColor={colors.primary}
                  iconMinus={IconMinus}
                  iconPlus={IconPlus}
                />
              </Box>
            )}
          </Box>
        </ImageInsertWrapper>
      )}
    </>
  );
};

export default React.memo(InsertImage);
